import { Controller } from "@hotwired/stimulus"
import ResourcePreviewPrefetch from "../lib/resource_preview_prefetch"

export default class extends Controller {
  static targets = ["backdrop", "panel", "frame", "title"]
  static values = {
    open: { type: Boolean, default: false },
    currentUrl: String,
  }

  connect() {
    this.prefetch = new ResourcePreviewPrefetch(this.frameTarget)
    this.boundOpen = this.open.bind(this)
    this.boundClose = this.close.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundPointerDown = this.handlePointerDown.bind(this)

    document.addEventListener("resource-preview:open", this.boundOpen)
    document.addEventListener("resource-preview:close", this.boundClose)
  }

  disconnect() {
    this.prefetch?.disconnect()
    document.removeEventListener("resource-preview:open", this.boundOpen)
    document.removeEventListener("resource-preview:close", this.boundClose)
    document.removeEventListener("keydown", this.boundKeydown, true)
    document.removeEventListener("pointerdown", this.boundPointerDown, true)
    clearTimeout(this.closeTimer)
  }

  open(event) {
    const url = event.detail?.url
    if (!url) return

    this.prefetch?.claim(url)

    this.previouslyFocusedElement = document.activeElement
    this.titleTarget.textContent = event.detail?.title || "Preview"
    this.show()
    this.setLoadingState()

    if (this.currentUrlValue === url && this.frameTarget.getAttribute("src") && typeof this.frameTarget.reload === "function") {
      this.frameTarget.reload()
    } else {
      this.currentUrlValue = url
      this.frameTarget.src = url
    }
  }

  close(event) {
    event?.preventDefault()
    if (!this.openValue) return

    this.openValue = false
    this.panelTarget.dataset.open = "false"
    this.backdropTarget.dataset.open = "false"
    document.removeEventListener("keydown", this.boundKeydown, true)
    document.removeEventListener("pointerdown", this.boundPointerDown, true)

    clearTimeout(this.closeTimer)
    this.closeTimer = setTimeout(() => {
      if (this.openValue) return

      this.panelTarget.classList.add("hidden")
      this.backdropTarget.classList.add("hidden")
      this.frameTarget.removeAttribute("src")
      this.frameTarget.innerHTML = ""
      this.panelTarget.setAttribute("aria-hidden", "true")
      document.dispatchEvent(new CustomEvent("resource-preview:closed"))

      if (this.previouslyFocusedElement?.focus) {
        this.previouslyFocusedElement.focus()
      }
    }, 200)
  }

  // The backdrop swallows clicks on narrow screens, but on wide ones it is
  // transparent and lets them through so the board behind stays usable. A click
  // out there should still put the drawer down.
  handlePointerDown(event) {
    if (!this.openValue || event.button !== 0) return

    const target = event.target
    if (!(target instanceof Element) || !target.isConnected) return
    // An open menu in the drawer (Set kind) folds away on a click outside it.
    this.panelTarget.querySelectorAll("details.kind-bulk[open]").forEach((menu) => {
      if (!menu.contains(target)) menu.open = false
    })
    if (this.panelTarget.contains(target)) return
    // A click that opens another preview, or one inside a modal or menu layered
    // over the page, is not a click away.
    if (target.closest('[data-preview-url], dialog[open], [role="dialog"], [aria-modal="true"], .modal')) return

    this.close()
  }

  handleKeydown(event) {
    if (!this.openValue || event.key !== "Escape") return
    if (this.hasOpenModal()) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this.close()
  }

  hasOpenModal() {
    return Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], [role="dialog"]'))
      .some((element) => !element.classList.contains("hidden") && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden")
  }

  show() {
    clearTimeout(this.closeTimer)
    this.openValue = true
    this.panelTarget.classList.remove("hidden")
    this.backdropTarget.classList.remove("hidden")
    this.panelTarget.setAttribute("aria-hidden", "false")
    document.addEventListener("keydown", this.boundKeydown, true)
    document.addEventListener("pointerdown", this.boundPointerDown, true)

    requestAnimationFrame(() => {
      this.panelTarget.dataset.open = "true"
      this.backdropTarget.dataset.open = "true"
      this.panelTarget.focus({ preventScroll: true })
      document.dispatchEvent(new CustomEvent("resource-preview:shown"))
    })
  }

  assignSelectedList(event) {
    event.preventDefault()

    const references = event.params.references?.split(",").filter(Boolean) || []
    document.dispatchEvent(
      new CustomEvent("tasks-table:open-assign-list", {
        detail: { references },
      }),
    )
  }

  // Start a triage run for the current task selection (handled by tasks-table,
  // which owns the selection state and the triage modal flow).
  triageSelected(event) {
    event.preventDefault()
    document.dispatchEvent(new CustomEvent("tasks-table:triage"))
  }

  // A row of the selection drawer's list: the table that owns the selection
  // opens that record in the drawer and rings its row.
  stepInto(event) {
    event.preventDefault()
    const { table, reference } = event.params
    if (!table || !reference) return
    document.dispatchEvent(new CustomEvent(`${table}:step-into`, { detail: { reference } }))
  }

  // A row's checked box takes it out of the selection; Clear empties it and
  // closes the drawer. The table that owns the selection does both.
  deselect(event) {
    event.preventDefault()
    const { table, reference } = event.params
    if (!table || !reference) return
    document.dispatchEvent(new CustomEvent(`${table}:deselect`, { detail: { reference } }))
  }

  clearSelection(event) {
    event.preventDefault()
    const { table } = event.params
    if (table) document.dispatchEvent(new CustomEvent(`${table}:clear-selection`))
    this.close()
  }

  // Hand a process multi-selection's bulk action off to the process-table
  // controller, which owns the selection state and the existing bulk flows
  // (state/reassign/reply modals, view tasks, download, assign list).
  dispatchProcessBulk(event) {
    event.preventDefault()

    const action = event.params.bulk
    if (!action) return

    const references = event.params.references?.split(",").filter(Boolean) || []
    document.dispatchEvent(
      new CustomEvent("process-table:bulk", {
        detail: { action, references, trigger: event.currentTarget },
      }),
    )
  }

  setLoadingState() {
    this.frameTarget.innerHTML = `
      <div class="p-4 space-y-4" role="status" aria-live="polite">
        <div class="skeleton h-6 w-2/3"></div>
        <div class="space-y-2">
          <div class="skeleton h-4 w-full"></div>
          <div class="skeleton h-4 w-5/6"></div>
          <div class="skeleton h-4 w-3/4"></div>
        </div>
        <div class="skeleton h-28 w-full rounded-lg"></div>
      </div>
    `
  }
}
